import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

const SCRIPT_SRC = "https://widgets.tradingview-widget.com/w/br/tv-technical-analysis.js";
let scriptPromise = null;

/** Carrega o Web Component uma única vez por sessão (evita instâncias duplicadas). */
export const preloadTrendWidget = () => {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.customElements?.get("tv-technical-analysis")) return resolve();
    const script = document.createElement("script");
    script.type = "module";
    script.src = SCRIPT_SRC;
    script.onload = resolve;
    script.onerror = () => reject(new Error("script indisponível"));
    document.head.appendChild(script);
  });
  return scriptPromise;
};

const CHECK_DELAY_MS = 9000;

/**
 * IA Tendência — análise técnica do ativo e timeframe atuais do gráfico.
 * Reutiliza a mesma instância do componente: só atualiza `symbol` e `interval`.
 */
export const TrendAnalysis = ({ tvSymbol, interval, onUnavailable }) => {
  const host = useRef(null);
  const element = useRef(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme() || { theme: "dark" };
  const isLight = theme === "light";

  useEffect(() => {
    let alive = true;
    let timer;
    setFailed(false);
    setLoading(true);

    preloadTrendWidget()
      .then(() => {
        if (!alive || !host.current) return;
        if (!element.current) {
          element.current = document.createElement("tv-technical-analysis");
          element.current.setAttribute("transparent", "");
          // Opção oficial: esconde a barra de intervalos do componente (o terminal já tem
          // a sua). O cabeçalho de atribuição é mantido — nada é escondido por CSS/DOM.
          element.current.setAttribute("hide-intervals", "");
          element.current.hideIntervals = true;
          element.current.style.width = "100%";
          element.current.style.display = "block";
          host.current.appendChild(element.current);
        }
        element.current.setAttribute("symbol", tvSymbol);
        element.current.setAttribute("interval", interval);
        setLoading(false);

        timer = setTimeout(() => {
          if (!alive) return;
          const node = element.current;
          const rendered = (node?.shadowRoot?.textContent || node?.textContent || "").trim();
          if (!rendered) {
            setFailed(true);
            onUnavailable?.(tvSymbol);
          }
        }, CHECK_DELAY_MS);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.warn("IA Tendência: componente não carregou", error);
        }
        if (alive) {
          setLoading(false);
          setFailed(true);
          onUnavailable?.(tvSymbol);
        }
      });

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [tvSymbol, interval, onUnavailable]);

  return (
    <div className="relative overflow-y-auto" data-testid="trend-analysis-body">
      <div ref={host} className={failed ? "hidden" : "min-h-[260px] px-1 pt-1"} />

      {loading && !failed ? (
        <div data-testid="trend-analysis-loading"
             className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          <span className={`h-6 w-6 animate-spin rounded-full border-2 border-t-transparent ${
            isLight ? "border-[#2563EB]" : "border-[#60A5FA]"}`} />
          <p className={`text-xs ${isLight ? "text-[#64748B]" : "text-[#8A97AD]"}`}>
            A carregar análise…
          </p>
        </div>
      ) : null}

      {failed ? (
        <div data-testid="trend-analysis-error"
             className="flex flex-col items-center gap-2 px-6 py-14 text-center">
          <AlertTriangle className="h-5 w-5 text-[#F59E0B]" />
          <p className={`text-sm ${isLight ? "text-[#0F172A]" : "text-white"}`}>
            Não foi possível carregar a análise neste momento.
          </p>
          <p className={`text-xs ${isLight ? "text-[#64748B]" : "text-[#8A97AD]"}`}>
            Tente novamente.
          </p>
        </div>
      ) : null}
    </div>
  );
};
